import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { PARTNERS } from '@/lib/constants';

export const TrustBar = () => {
  const allPartners = [...PARTNERS.airlines, ...PARTNERS.associations];

  return (
    <section data-testid="trust-bar" className="bg-[#F5F5F5] py-8 overflow-hidden">
      <div className="container-custom mb-5">
        <p className="text-center text-[10px] font-bold tracking-[0.3em] uppercase text-[#4A5568]/40">
          Trusted By Leading Airlines &amp; Associations
        </p>
      </div>
      <div className="marquee-mask">
        <Marquee speed={30} gradient={false} pauseOnHover>
          {allPartners.map((partner, index) => (
            <div
              key={index}
              className="mx-5 flex items-center gap-3 px-5 py-3 bg-white border border-slate-200/60 rounded-sm hover:border-[#D4AF37]/40 hover:shadow-sm transition-all duration-300"
            >
              <img
                src={partner.logo}
                alt=""
                className="w-6 h-6 object-contain"
                loading="lazy"
              />
              <span className="text-[#0A1F3D] text-sm font-semibold whitespace-nowrap tracking-wide">
                {partner.name}
              </span>
            </div>
          ))}
        </Marquee>
      </div>
    </section>
  );
};

export const StatsSection = ({ stats, dark = false }) => {
  return (
    <section
      data-testid="stats-section"
      className={`py-16 ${dark ? 'bg-[#0A1F3D]' : 'bg-white'}`}
    >
      <div className="container-custom">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center"
            >
              <div className={`stat-number text-4xl md:text-5xl mb-2 ${dark ? 'text-[#D4AF37]' : 'text-[#0A1F3D]'}`}>
                {stat.number}
              </div>
              <div className={`text-sm ${dark ? 'text-white/60' : 'text-[#4A5568]'}`}>
                {stat.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const SectionHeader = ({ overline, title, description, centered = true, light = false }) => {
  return (
    <div className={`mb-12 md:mb-16 ${centered ? 'text-center max-w-3xl mx-auto' : ''}`}>
      {overline && (
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-xs font-bold tracking-[0.2em] uppercase text-[#D4AF37] mb-4 block"
        >
          {overline}
        </motion.span>
      )}
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 }}
        className={`text-2xl md:text-4xl lg:text-5xl font-bold font-['Playfair_Display'] tracking-tight mb-4 ${
          light ? 'text-white' : 'text-[#0A1F3D]'
        }`}
      >
        {title}
      </motion.h2>
      {description && (
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className={`text-base md:text-lg leading-relaxed ${light ? 'text-white/70' : 'text-[#4A5568]'}`}
        >
          {description}
        </motion.p>
      )}
    </div>
  );
};

export const PageHero = ({ title, subtitle, backgroundImage, children }) => {
  return (
    <section
      data-testid="page-hero"
      className="relative min-h-[40vh] md:min-h-[50vh] flex items-center justify-center overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={backgroundImage}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0A1F3D]/80" />
      </div>

      {/* Content */}
      <div className="relative z-10 container-custom text-center py-20 md:py-32">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-3xl md:text-5xl lg:text-6xl font-bold font-['Playfair_Display'] text-white mb-4 md:mb-6"
        >
          {title}
        </motion.h1>
        {subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto"
          >
            {subtitle}
          </motion.p>
        )}
        {children}
      </div>
    </section>
  );
};
